import NextApp from 'next/app';
import Head from 'next/head';
import _, { partial } from 'lodash';
import { Provider as ReduxProvider } from 'react-redux';
import Router from 'next/router';
import { compose } from 'redux';
import React from 'react';
import Debug from 'debug';
import moment from 'moment';
import accepts from 'accepts';
import getConfig from 'next/config';
import PageProgress from 'lib/components/PageProgress';
import { I18nProvider } from 'lib/contexts/i18n';
import { TrackerProvider } from 'lib/contexts/tracker';
import withErrorHandling from 'lib/hocs/withErrorHandling';
import ErrorBox from 'lib/hocs/withErrorHandling/ErrorBox';
import tracker from 'lib/utils/userTracking/tracker';
import { isServer } from 'lib/common/constants';

import ToastManager from '../ToastManager';
import Wootric from '../Wootric';
import { IntercomProvider } from '../Intercom/IntercomProvider';
import withI18n from './lib/withI18n';
import withReduxStore from './lib/withReduxStore';

const { publicRuntimeConfig } = getConfig();

// `WithMonitorComponentDidCatch` is kept for consistency on sentry
const options = {
  tags: { from: 'WithMonitorComponentDidCatch' },
  ErrorComponent: (props) => ErrorBox({ spacing: 'large', ...props }),
};
const withTaggedErrorHandling = partial(withErrorHandling, _, options);

const debug = Debug('rs:component/App');

class App extends NextApp {
  /**
   * Called once on the initial app entry-point (server-side)
   * and each time the route changes thereafter (client-side).
   *
   * @param {Object} appContext
   */
  static async getInitialProps({ Component, ctx }) {
    debug('get initial props: %s', ctx.pathname);

    // call the current Page's getInitialProps()
    const pageProps = Component.getInitialProps
      ? await Component.getInitialProps(ctx)
      : {};

    const wootricLang = ctx.req
      ? accepts(ctx.req).language([
          ctx.req.language,
          ...Object.keys(Wootric.supportedLanguages),
        ])
      : 'en';

    // the object returned here becomes the stringified
    // and inlined value of `window.__NEXT_DATA__.props` that
    // gets passed down to the client inside a <script>
    return {
      pageProps,
      wootricLang,
    };
  }

  /**
   * Defines the initial redux state, based on data
   * within the first HTTP request for the app.
   *
   * This forms the basis of the redux store that will be passed
   * to the client and live for the lifetime of the app.
   *
   * @param {Object} ctx
   */
  static getInitialReduxState({ req, query }) {
    debug('get initial redux state', query);

    const { headers = [], connection = {}, cookies = {} } = req;

    // FIXME: `encrypted` is not a documented API, we should eliminate the need
    // to reconstruct the origin entirely to fix this
    const protocol = connection.encrypted ? 'https:' : 'http:';
    // NOTE: host is a misnomer, it's really origin
    const host = `${protocol}//${headers.host}`;
    const token = cookies.token;
    const userAgent = headers['user-agent'];

    // SSO users are able to specify a organization-ID via a query
    // param on the initial app request url. This is later passed
    // to all API requests and used to determine their
    // scope/context when navigating around the app
    let { orgId, ssoOrgId } = query;

    if (orgId && orgId.toLowerCase() === 'all') orgId = null;
    if (orgId) ssoOrgId = orgId;

    const result = {
      request: {
        ssoOrgId,
        host,
        token,
        userAgent,
        countryCode: headers['cf-ipcountry'] || 'GB',
      },
    };

    debug('initial props:', result);

    return result;
  }

  static setMomentLocale(language) {
    let momentLanguage = language;

    // `moment` requires the specific dialect of Chinese that we don't use throughout the app
    if (momentLanguage === 'zh') momentLanguage = 'zh-hk';

    // Set the global moment locale to the app language
    moment.locale(momentLanguage);
  }

  /**
   * Runs once on the server and once on the client when
   * the App is created. The `props` argument is the
   * return value of the above `getInitialProps()`.
   *
   * @param {Object} props
   */
  constructor(props) {
    debug('constructor');
    super(props);

    App.setMomentLocale(props.i18n.language);
  }

  componentDidMount() {
    Router.events.on('routeChangeStart', this.onRouteChangeStart);
    Router.events.on('routeChangeComplete', this.onRouteChangeComplete);
    Router.events.on('routeChangeError', this.onRouteChangeError);
  }

  componentWillUnmount() {
    Router.events.off('routeChangeStart', this.onRouteChangeStart);
    Router.events.off('routeChangeComplete', this.onRouteChangeComplete);
    Router.events.off('routeChangeError', this.onRouteChangeError);
  }

  trackPageLoad = () => {
    if (!isServer) {
      tracker.track('page_loaded', {
        search: window.location.search,
        title: document.title,
        referrer: document.referrer,
        path: window.location.pathname,
        normalized_path: Router.pathname,
        url: window.location.href,
      });
    }
  };

  onRouteChangeStart = () => {
    debug('on route change start');
    PageProgress.start();
  };

  onRouteChangeComplete = () => {
    debug('on route change complete');
    this.trackPageLoad();
    PageProgress.done();
  };

  onRouteChangeError = () => {
    debug('on route change error');
    PageProgress.done();
  };

  render() {
    const { Component, pageProps, store, i18n, wootricLang } = this.props;

    return (
      <>
        <Head>
          <title>Deliveroo | Restaurant Hub</title>
          <meta name="description" content={i18n.gettext('meta.description')} />
        </Head>

        <I18nProvider i18n={i18n}>
          <ReduxProvider store={store}>
            <TrackerProvider tracker={tracker}>
              <IntercomProvider appId="xzt1payv">
                <Component {...pageProps} />
                <ToastManager />
                {publicRuntimeConfig.enableWootric && (
                  <Wootric language={wootricLang} />
                )}
              </IntercomProvider>
            </TrackerProvider>
          </ReduxProvider>
        </I18nProvider>
      </>
    );
  }
}

export default compose(withI18n, withTaggedErrorHandling, withReduxStore)(App);

export { App }; // for testing
